import { useWatch } from 'antd/es/form/Form'
import useForm from 'antd/es/form/hooks/useForm'
import { observer } from 'mobx-react-lite'
import { ChangeEvent, Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { SelectInstance } from 'react-select'

import { useStore } from 'store/useStore'
import { countries } from 'sections/LKSection/modules/UserModule/data'
import { CountrySelectWithFlags } from 'sections/PromoSection/pages/PromoPage/shared/OpenPickPointForm/components/customSelect/CountrySelectWithFlags'
import { countrySelectCustomStyles } from 'sections/PromoSection/pages/PromoPage/shared/OpenPickPointForm/countrySelectCustomStyles'
import { CountryItem } from 'components/CountryPhoneInput/CountryPhoneInput'
import { ButtonPromo, TextPromo } from 'components/ui'
import { Spinner } from 'components/ui/Spinner/Spinner'
import { Checkbox, Flex, Form, FormItem, Input, TextArea } from 'components/uiKit'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { useResize } from 'utils/hooks/useResize'
import { NOT_NUMBERS_REGEXP, TELEGRAM_REGEXP } from 'shared/constants/regExp'
import { PromoRoutesEnum } from 'shared/constants/routes'
import { BaseOption } from 'shared/types/types'
import { PromoSiteContactRequest } from 'types/types'

import { errorMessages, phoneValidationRules } from './data'
import { FeedbackFormValues } from './types'

import ErrorIcon from 'assets/img/feedback/feedbackForm-error.svg?react'
import SuccessIcon from 'assets/img/feedback/feedbackForm-success.svg?react'
import s from './FeedbackForm.module.scss'

type PropsType = {
  shouldReset: boolean
  isFormVisible: boolean
  isResolveVisible: boolean
  isRejectVisible: boolean
  isPending: boolean
  setIsFormVisible?: Dispatch<SetStateAction<boolean>>
  addReview: (value: PromoSiteContactRequest) => void
}

export const FeedbackForm = observer(
  ({ isResolveVisible, isRejectVisible, isPending, setIsFormVisible, addReview }: PropsType) => {
    const { isSDesktop, isDesktop } = useResize()
    const { searchEngineUiStore: uiStore } = useStore()
    const [form] = useForm()
    const { t } = useTranslation()

    const [fieldStates, setFieldStates] = useState({
      phone: true,
      email: true,
      telegram: true,
      message: true,
    })
    const [submittable, setSubmittable] = useState(false)
    const values = useWatch([], form)
    const selectRef = useRef<SelectInstance<any, boolean> | null>(null)
    const [selectedCountry, setSelectedCountry] = useState<BaseOption<CountryItem> | null>(null)
    const [phoneError, setPhoneError] = useState(false)

    const localizedCountries = useMemo(() => {
      countries.sort((a, b) => a.label.localeCompare(b.label))
      return countries.map((country) => ({
        ...country,
        label: t(getSharedKey(country.value.label)),
      }))
    }, [t])

    useEffect(() => {
      form
        .validateFields({ validateOnly: true })
        .then(() => setSubmittable(true))
        .catch(() => setSubmittable(false))
    }, [form, values])

    useEffect(() => {
      if (!localizedCountries.length) return

      const computedDefaultOption = localizedCountries[5]

      setSelectedCountry(computedDefaultOption)
    }, [localizedCountries])

    const handleFinish = (values: FeedbackFormValues) => {
      let phoneNumber = values.phone.trim()

      if (selectedCountry && selectedCountry.value && selectedCountry.value.value) {
        phoneNumber = `+${selectedCountry.value.value}${phoneNumber}`
      }

      const requestData: PromoSiteContactRequest = {
        phoneNumber: phoneNumber,
        email: values.email.trim(),
        telegram: values.telegram.trim(),
        message: values.message.trim(),
      }

      addReview(requestData)
      form.resetFields()
    }

    const handleInputChange = (fieldName: string, e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value

      setFieldStates((prevState) => ({
        ...prevState,
        [fieldName]: value === '',
      }))

      form
        .validateFields(['phone'])
        .then(() => {
          setPhoneError(false)
        })
        .catch(() => {
          const hasError = form.getFieldError('phone').length > 0
          setPhoneError(hasError)
        })
    }

    const handleCloseTicketButton = () => {
      setIsFormVisible?.(false)
      uiStore.setIsRejectVisible(false)
      uiStore.setIsResolveVisible(false)
      form.resetFields()
      setFieldStates({
        phone: true,
        email: true,
        telegram: true,
        message: true,
      })
    }

    const handleCountryChange = (item: BaseOption<CountryItem>) => {
      setSelectedCountry(item)
    }

    const closeFormButton = () => {
      return (
        <ButtonPromo
          onClick={() => handleCloseTicketButton()}
          fullWidth={true}
          variant="purple"
          className={s.closeButton}
        >
          Закрыть
        </ButtonPromo>
      )
    }

    return (
      <>
        {!(isResolveVisible || isRejectVisible) && (
          <>
            <p className={s.title}>
              Заполните форму ниже — менеджер свяжется с вами и ответит на вопросы о том, как открыть пункт выдачи
              заказов Wildberries
            </p>
            <Form
              className={s.form}
              form={form}
              onFinish={handleFinish}
              initialValues={{ phone: '', email: '', telegram: '', message: '', isCheckboxChecked: false }}
            >
              <FormItem
                name="phone"
                validateTrigger={['onChange', 'onBlur']}
                rules={[
                  { required: true, message: errorMessages.phoneRequired },
                  {
                    validator: (_, value) => {
                      const countryCode = selectedCountry?.value?.country

                      if (!countryCode) {
                        return Promise.reject(new Error(errorMessages.phoneInvalid))
                      }

                      const validationRule = phoneValidationRules[countryCode as keyof typeof phoneValidationRules]

                      if (!validationRule) {
                        return Promise.reject(new Error(errorMessages.phoneInvalid))
                      }

                      const { minLength, maxLength } = validationRule

                      const isNumeric = !NOT_NUMBERS_REGEXP.test(value)

                      if (value && isNumeric && value.length >= minLength && value.length <= maxLength) {
                        return Promise.resolve()
                      }

                      return Promise.reject(new Error(errorMessages.phoneInvalid))
                    },
                  },
                ]}
              >
                <div className={s.inputWrapper}>
                  <div className={`${s.inputPhoneWrapper} ${phoneError ? s.error : ''}`}>
                    <CountrySelectWithFlags
                      menuPosition="absolute"
                      styles={countrySelectCustomStyles({
                        menu: { margin: isSDesktop || isDesktop ? 5 : 0, 'margin-top': 10 },
                      })}
                      isSearchable={false}
                      components={{}}
                      ref={selectRef}
                      value={selectedCountry}
                      options={localizedCountries}
                      className={s.customSelect}
                      onChange={handleCountryChange}
                    />
                    <Flex align="center" flex={1} style={{ height: '100%', position: 'relative' }}>
                      <span className={s.phonePrefix}>{`+${selectedCountry?.value.value}`}</span>
                      {fieldStates.phone && (
                        <div className={s.placeholder} style={{ left: '60px' }}>
                          <span className={s.placeholderText}>Номер телефона</span>
                          <span className={s.required}>*</span>
                        </div>
                      )}

                      <Input
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className={s.inputPhone}
                        required
                        value={form.getFieldValue('phone') || ''}
                        onChange={(e) => handleInputChange('phone', e)}
                        style={{ border: 'none', outline: 'none', boxShadow: 'none' }}
                      />
                    </Flex>
                  </div>
                </div>
              </FormItem>
              <FormItem
                name="email"
                rules={[
                  { type: 'email', message: errorMessages.emailInvalid },
                  { required: true, message: errorMessages.emailRequired },
                ]}
              >
                <div className={s.inputWrapper}>
                  {fieldStates.email && (
                    <div className={s.placeholder}>
                      <span className={s.placeholderText}>Почта</span>
                      <span className={s.required}>*</span>
                    </div>
                  )}
                  <Input className={s.input} type="email" onChange={(e) => handleInputChange('email', e)} required />
                </div>
              </FormItem>
              <FormItem
                name={'telegram'}
                rules={[
                  { pattern: TELEGRAM_REGEXP, message: errorMessages.telegramInvalid },
                  { required: true, message: errorMessages.telegramRequired },
                ]}
              >
                <div className={s.inputWrapper}>
                  {fieldStates.telegram && (
                    <div className={s.placeholder}>
                      <span className={s.placeholderText}>Ник в Telegram</span>
                      <span className={s.required}>*</span>
                    </div>
                  )}
                  <Input className={s.input} onChange={(e) => handleInputChange('telegram', e)} />
                </div>
              </FormItem>
              <FormItem
                name="message"
                rules={[
                  { required: true, message: errorMessages.messageRequired },
                  { min: 10, message: errorMessages.messageTooShort },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim().length === 0) {
                        return Promise.reject(new Error(errorMessages.messageWithOnlySpaces))
                      }
                      return Promise.resolve()
                    },
                  },
                ]}
              >
                <div className={s.textAreaWrap}>
                  {fieldStates.message && (
                    <div className={s.placeholder}>
                      <span className={s.placeholderText}>Какой у вас вопрос?</span>
                      <span className={s.required}>*</span>
                    </div>
                  )}
                  <TextArea
                    className={s.textarea}
                    rows={2}
                    maxLength={500}
                    minLength={10}
                    showCount
                    onChange={(e) => handleInputChange('message', e)}
                  />
                </div>
              </FormItem>
              <FormItem
                name="isCheckboxChecked"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value ? Promise.resolve() : Promise.reject('Вы должны подтвердить согласие'),
                  },
                ]}
              >
                <div className={s.checkboxWrap}>
                  <Checkbox className={s.checkbox} required={true}>
                    <TextPromo size="s12" color="gray" fontFamily="ASLHauss">
                      Я ознакомился с{' '}
                      <Link to={PromoRoutesEnum.privacyPolicy} target="_blank" className={s.link} rel="noreferrer">
                        Политикой в отношении обработки персональных данных работников и контрагентов
                      </Link>{' '}
                      и даю свое согласие на обработку данных, указанных в этой форме ООО «РВБ» (ИНН 9714053621) и его
                      аффилированным компаниям для цели взаимодействия со мной по поводу рассмотрения обращения, на срок
                      рассмотрения, если оно не будет отозвано раньше путем направления письменного заявления.
                    </TextPromo>
                  </Checkbox>
                </div>
              </FormItem>
              <FormItem>
                <ButtonPromo className={s.button} variant="purple" fullWidth={true} disabled={!submittable}>
                  {isPending ? <Spinner color="white" size={24} /> : 'Отправить'}
                </ButtonPromo>
              </FormItem>
            </Form>
          </>
        )}

        {isResolveVisible && (
          <div className={s.result}>
            <div className={s.wrapper}>
              <SuccessIcon className={s.icon} />
              <h3 className={s.title}>Получили ваш запрос</h3>
              <p className={s.caption}>
                Спасибо! Менеджер свяжется с вами по телефону, в Telegram или по почте в ближайшее время
              </p>
            </div>
            {closeFormButton()}
          </div>
        )}

        {isRejectVisible && (
          <div className={s.result}>
            <div className={s.wrapper}>
              <ErrorIcon className={s.icon} />
              <h3 className={s.title}>Не получилось отправить</h3>
              <p className={s.caption}>Попробуйте обновить страницу или заполнить форму ещё раз позже</p>
            </div>
            {closeFormButton()}
          </div>
        )}
      </>
    )
  },
)
